import React from "react";
import { Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Activity, AlertTriangle, GitMerge, ShieldCheck } from "lucide-react";
import { ConflictsPage } from "./pages/ConflictsPage";
import { TrustedCatalogPage } from "./pages/TrustedCatalogPage";
import { DriftPage } from "./pages/DriftPage";
import { TourLauncher, TourOverlay, tourForPath, useAutoStartTour } from "./components/tour";
import { semanticHealth } from "./lib/api";

export function App() {
  return (
    <div className="grid min-h-screen grid-rows-[auto,1fr] bg-semantic-fog">
      <Header />
      <main className="min-h-0">
        <Routes>
          <Route path="/" element={<Navigate to="/conflicts" replace />} />
          <Route path="/conflicts" element={<ConflictsPage />} />
          <Route path="/trusted" element={<TrustedCatalogPage />} />
          <Route path="/drift" element={<DriftPage />} />
        </Routes>
      </main>
      <RouteTourBinder />
      <TourOverlay />
    </div>
  );
}

function RouteTourBinder() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname);
  if (!tour) return null;
  return <BoundTour key={tour.id} />;
}

function BoundTour() {
  const { pathname } = useLocation();
  const tour = tourForPath(pathname)!;
  useAutoStartTour(tour);
  return null;
}

function Header() {
  // The /health endpoint returns the IR version, which is a real
  // signal that "this build is alive and which IR version it speaks."
  // Surfaces in the header as a sanity-check badge.
  const { data: health } = useQuery({
    queryKey: ["semantic-health"],
    queryFn: () => semanticHealth(),
    staleTime: 60_000,
    retry: false,
  });

  return (
    <header className="sticky top-0 z-20 border-b border-semantic-slate/25 bg-white/85 backdrop-blur">
      <div className="flex w-full items-center gap-6 px-6 py-2.5">
        <a href="/" className="flex items-center gap-2.5 font-semibold text-semantic-ink">
          <Logo />
          <div className="leading-tight">
            <div className="font-display text-[15px]">DatumCraft Semantic</div>
            <div className="text-2xs font-normal text-semantic-slate">
              One trusted definition per metric, across every BI platform
            </div>
          </div>
        </a>
        <nav aria-label="primary" data-tour-id="nav" className="flex gap-1 text-sm">
          <NavItem to="/conflicts" icon={<GitMerge size={14} />}>Conflicts</NavItem>
          <NavItem to="/trusted" icon={<ShieldCheck size={14} />}>Trusted catalog</NavItem>
          <NavItem to="/drift" icon={<AlertTriangle size={14} />}>Drift</NavItem>
        </nav>
        <div className="ml-auto flex items-center gap-2 text-2xs text-semantic-slate">
          {health?.irVersion && (
            <span
              title="Semantic IR schema version this server speaks"
              className="inline-flex items-center gap-1 rounded-full bg-semantic-primary/10 px-2 py-0.5 font-medium text-semantic-primary"
            >
              <Activity size={11} /> IR {health.irVersion}
            </span>
          )}
          <TourLauncher />
        </div>
      </div>
    </header>
  );
}

function NavItem({
  to,
  icon,
  children,
}: {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 font-medium transition-colors ${
          isActive
            ? "bg-semantic-primary text-white shadow-sm"
            : "text-semantic-ink hover:bg-semantic-mist"
        }`
      }
    >
      <span aria-hidden>{icon}</span>
      <span>{children}</span>
    </NavLink>
  );
}

// Product glyph — converging arrows pointing at a center point
// (variants → trusted definition).
function Logo() {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-semantic-ink text-white shadow-sm"
      aria-hidden
    >
      <svg
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="2.4" fill="#34d399" />
        <path d="M5 6 L10 11" stroke="currentColor" strokeWidth={1.6} />
        <path d="M19 6 L14 11" stroke="currentColor" strokeWidth={1.6} />
        <path d="M5 18 L10 13" stroke="currentColor" strokeWidth={1.6} />
        <path d="M19 18 L14 13" stroke="currentColor" strokeWidth={1.6} />
      </svg>
    </span>
  );
}
